/*
 * PRISM product form — variant selection + AJAX add to cart.
 *
 * <prism-variant-selects> resolves the selected variant from the option inputs,
 * keeps the form's hidden id + the URL in sync, announces the change via the
 * variantChange event, and refreshes server-rendered regions (price, inventory,
 * availability) through the Section Rendering API so money/locale stay correct.
 *
 * <prism-product-form> intercepts the add-to-cart submit, posts through the
 * PRISM cart engine, and opens the drawer.
 *
 * Markup contracts:
 *   <prism-variant-selects data-section="{id}" data-url="{product.url}" data-update-url>
 *     <script type="application/json" data-prism-variants>{{ product.variants | json }}</script>
 *     <select data-option-position="1">…</select>           (or)
 *     <input type="radio" data-option-position="1" value="…">
 *   </prism-variant-selects>
 *
 *   <prism-product-form data-section="{id}">
 *     <form data-prism-product-form data-section="{id}" action="{routes.cart_add_url}">
 *       <input type="hidden" name="id" value="{variant.id}">
 *       <input name="quantity" value="1">
 *       <button type="submit" data-add-button><span data-add-text>Add to cart</span></button>
 *       <div data-form-error role="alert"></div>
 *     </form>
 *   </prism-product-form>
 *
 *   Refreshable regions inside the section carry: data-variant-refresh="price" (etc.)
 */
(function () {
  'use strict';

  function sectionRoot(sectionId) {
    return document.getElementById('shopify-section-' + sectionId) || document;
  }

  function drawerSections() {
    var drawer = document.querySelector('prism-cart-drawer[data-cart-section]');
    return drawer ? [drawer.getAttribute('data-cart-section')] : undefined;
  }

  if (!customElements.get('prism-variant-selects')) {
    customElements.define(
      'prism-variant-selects',
      class PrismVariantSelects extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.getAttribute('data-section');
          this.productUrl = this.getAttribute('data-url');
          this.updateUrl = this.hasAttribute('data-update-url');
          this.variants = this.readVariants();

          this.onChange = this.onChange.bind(this);
          this.addEventListener('change', this.onChange);
        }

        disconnectedCallback() {
          this.removeEventListener('change', this.onChange);
        }

        readVariants() {
          var tag = this.querySelector('script[data-prism-variants]');
          if (!tag) return [];
          try {
            return JSON.parse(tag.textContent);
          } catch (e) {
            return [];
          }
        }

        selectedOptions() {
          var inputs = this.querySelectorAll('[data-option-position]');
          var byPosition = [];
          inputs.forEach(function (input) {
            var pos = parseInt(input.getAttribute('data-option-position'), 10) - 1;
            if (input.tagName === 'SELECT') {
              byPosition[pos] = input.value;
            } else if (input.type === 'radio' && input.checked) {
              byPosition[pos] = input.value;
            }
          });
          return byPosition;
        }

        findVariant() {
          var selected = this.selectedOptions();
          return (
            this.variants.find(function (variant) {
              return variant.options.every(function (value, index) {
                return value === selected[index];
              });
            }) || null
          );
        }

        onChange() {
          var variant = this.findVariant();
          this.updateForm(variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.sectionId, variant: variant },
          });

          if (!variant) return;
          if (this.updateUrl && this.productUrl) {
            window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
          }
          this.refreshRegions(variant);
        }

        updateForm(variant) {
          var form = document.querySelector(
            'form[data-prism-product-form][data-section="' + this.sectionId + '"]'
          );
          if (!form) return;
          var idInput = form.querySelector('[name="id"]');
          if (idInput) idInput.value = variant ? variant.id : '';
          var button = form.querySelector('[data-add-button]');
          if (button) button.disabled = !variant || !variant.available;
        }

        refreshRegions(variant) {
          if (!this.productUrl) return;
          var url = this.productUrl + '?variant=' + variant.id + '&section_id=' + this.sectionId;
          fetch(url)
            .then(function (r) {
              return r.text();
            })
            .then((html) => {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var root = sectionRoot(this.sectionId);
              root.querySelectorAll('[data-variant-refresh]').forEach(function (el) {
                var key = el.getAttribute('data-variant-refresh');
                var fresh = doc.querySelector('[data-variant-refresh="' + key + '"]');
                if (fresh) el.innerHTML = fresh.innerHTML;
              });
            })
            .catch(function () {});
        }
      }
    );
  }

  if (!customElements.get('prism-product-form')) {
    customElements.define(
      'prism-product-form',
      class PrismProductForm extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form[data-prism-product-form]');
          if (!this.form) return;
          this.button = this.form.querySelector('[data-add-button]');
          this.errorTarget = this.form.querySelector('[data-form-error]');
          this.onSubmit = this.onSubmit.bind(this);
          this.form.addEventListener('submit', this.onSubmit);
        }

        disconnectedCallback() {
          if (this.form) this.form.removeEventListener('submit', this.onSubmit);
        }

        onSubmit(e) {
          if (!window.PRISM || !window.PRISM.cart) return; // graceful: native submit
          e.preventDefault();
          var idInput = this.form.querySelector('[name="id"]');
          if (!idInput || !idInput.value) return;

          this.setLoading(true);
          this.clearError();

          var item = { id: idInput.value, quantity: this.quantity(), properties: this.properties() };
          window.PRISM.cart
            .add(item, { sections: drawerSections(), source: 'add' })
            .catch((err) => {
              this.showError(err && err.message);
            })
            .then(() => {
              this.setLoading(false);
            });
        }

        quantity() {
          var input = this.form.querySelector('[name="quantity"]');
          return input ? Math.max(1, parseInt(input.value, 10) || 1) : 1;
        }

        properties() {
          var props = {};
          this.form.querySelectorAll('[name^="properties["]').forEach(function (input) {
            if (input.type === 'checkbox' && !input.checked) return;
            var match = input.name.match(/^properties\[(.*)\]$/);
            if (match) props[match[1]] = input.value;
          });
          return Object.keys(props).length ? props : undefined;
        }

        setLoading(state) {
          if (!this.button) return;
          this.button.classList.toggle('prism-loading', state);
          this.button.setAttribute('aria-busy', state ? 'true' : 'false');
        }

        clearError() {
          if (this.errorTarget) this.errorTarget.textContent = '';
        }

        showError(message) {
          if (this.errorTarget && message) this.errorTarget.textContent = message;
        }
      }
    );
  }
})();
